import React, { useState, useEffect } from 'react';
import { Popover, Box, IconButton, Typography, Button } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { TIME_FORMATS } from './appConstants';
import './TimePickerModal.scss';

/**
 * Convert a 24-hr "HH:MM" string to a 12-hr "H:MM AM/PM" display string.
 * Exported so consumers can reuse it without duplicating logic.
 */
export const format12h = (hhMm) => {
  if (!hhMm) return '';
  const [hStr, mStr] = hhMm.split(':');
  const h = parseInt(hStr, 10);
  const m = String(mStr || '00').padStart(2, '0');
  const suffix = h < 12 ? TIME_FORMATS.AM : TIME_FORMATS.PM;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m} ${suffix}`;
};

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];

/**
 * TimePickerModal
 *
 * Props:
 *  - open        {boolean}  whether the popover is visible
 *  - anchorEl    {element}  anchor for the Popover
 *  - onClose     {func}     called when user closes
 *  - value       {string}   current time string "HH:MM"
 *  - onChange    {func}     called with new time string "HH:MM"
 *  - title       {string}   optional header label (defaults to date+time display)
 *  - currentDate {Date}     optional date to show in header (e.g. "17.08.2026")
 */
export const TimePickerInline = ({
  value,
  onChange,
  /** Optional plain-text header label. If omitted, nothing is shown in the header. */
  headerLabel,
}) => {
  const parseTime = (val) => {
    if (!val) {
      const now = new Date();
      return {
        hour: String(now.getHours()).padStart(2, '0'),
        minute: String(now.getMinutes()).padStart(2, '0')
      };
    }
    const [h, m] = val.split(':');
    return {
      hour: String(h || '0').padStart(2, '0'),
      minute: String(m || '0').padStart(2, '0'),
    };
  };

  const [selectedHour, setSelectedHour] = useState('00');
  const [selectedMinute, setSelectedMinute] = useState('00');

  // Sync internal state when value changes
  useEffect(() => {
    if (value) {
      const parsed = parseTime(value);
      setSelectedHour(parsed.hour);
      // Snap minute to nearest valid 5-min slot
      const rawMin = parseInt(parsed.minute, 10);
      const snapped = MINUTES.reduce((prev, cur) =>
        Math.abs(parseInt(cur, 10) - rawMin) < Math.abs(parseInt(prev, 10) - rawMin) ? cur : prev
      );
      setSelectedMinute(snapped);
    } else {
      setSelectedHour('');
      setSelectedMinute('');
    }
  }, [value]);

  const handleHourClick = (h) => {
    setSelectedHour(h);
    onChange?.(`${h}:${selectedMinute || '00'}`);
  };

  const handleMinuteClick = (m) => {
    setSelectedMinute(m);
    onChange?.(`${selectedHour || '00'}:${m}`);
  };

  return (
    <TimePickerContent 
      selectedHour={selectedHour}
      selectedMinute={selectedMinute}
      handleHourClick={handleHourClick}
      handleMinuteClick={handleMinuteClick}
    />
  );
};

export const TimePickerContent = ({ selectedHour, selectedMinute, handleHourClick, handleMinuteClick }) => {
  const { t } = useTranslation();
  return (
    <Box className="time-picker-modal">
        {/* Body: Hours + Minutes side by side */}
        <Box className="time-picker-body">
          {/* Hours section */}
          <Box className="time-picker-section time-picker-section--hours">
            <Typography className="time-picker-section-title">{t('timePicker.hours')}</Typography>
            <Box className="time-picker-grid time-picker-grid--hours">
              {HOURS.map((h) => (
                <Box
                  key={h}
                  className={`time-picker-cell${selectedHour === h ? ' is-selected' : ''}`}
                  onClick={() => handleHourClick(h)}
                >
                  {h}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Minutes section */}
        <Box className="time-picker-section time-picker-section--minutes">
            <Typography className="time-picker-section-title">{t('timePicker.minutes')}</Typography>
            <Box className="time-picker-grid time-picker-grid--minutes">
              {MINUTES.map((m) => (
                <Box
                  key={m}
                  className={`time-picker-cell${selectedMinute === m ? ' is-selected' : ''}`}
                  onClick={() => handleMinuteClick(m)}
                >
                  {m}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
  );
};

const TimePickerModal = (props) => {
  const { t } = useTranslation();

  const handleApply = () => {
    props.onClose && props.onClose();
  };

  const handleClear = () => {
    if (props.onChange) props.onChange('');
    props.onClose && props.onClose();
  };

  return (
    <Popover
      open={props.open}
      anchorEl={props.anchorEl}
      onClose={props.onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      marginThreshold={4}
      PaperProps={{ 
        className: 'time-picker-popover date-picker-popover'
      }}
    >
      <Box className="date-picker-modal time-picker-modal-wrapper">
        <Box className="date-picker-header">
          <Typography variant="h6" className="date-picker-title">
            {t('timePicker.selectTime')}
          </Typography>
          <IconButton onClick={props.onClose} size="small">
            <Close />
          </IconButton>
        </Box>
        <Box className="date-picker-header-divider" />
        
        <Box className="date-picker-range-column time-picker-range-wrapper">
          <TimePickerInline 
            value={props.value} 
            onChange={(val) => {
              if (props.onChange) props.onChange(val);
            }} 
          />
        </Box>

        <Box className={`date-picker-actions time-picker-actions-wrapper ${props.showClear ? 'has-clear-btn' : 'date-picker-actions-right'}`} style={props.showClear ? { display: 'flex', justifyContent: 'space-between', width: '100%' } : {}}>
          {props.showClear && (
            <Button onClick={handleClear} color="error">
              {t('common.clear')}
            </Button>
          )}
          <Box>
            <Button onClick={props.onClose}>{t('calendar.cancel')}</Button>
            <Button variant="contained" onClick={handleApply} style={{ marginLeft: 8 }}>
              {t('calendar.apply')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Popover>
  );
};

export default TimePickerModal;
